/**
 * useWorkspaceTwoFactorPolicy — React hook for workspace 2FA policy data
 *
 * sl-d6mh.4.1: Provides grace period info and enforcement status
 * from the backend workspacePolicyQuery.
 *
 * Skips the GraphQL query for local-flavour workspaces.
 */
import { useQuery } from '@affine/core/components/hooks/use-query';
import { workspacePolicyQuery } from '@affine/graphql';
import { useMemo } from 'react';

export interface WorkspaceTwoFactorPolicy {
  requireTOTP: boolean;
  allowEmailOTP: boolean;
  enforcedAt: string | null;
  gracePeriodDays: number;
  gracePeriodActive: boolean;
  gracePeriodRemainingDays: number | null;
  isLoading: boolean;
  error: Error | null;
}

const DEFAULT_POLICY: WorkspaceTwoFactorPolicy = {
  requireTOTP: false,
  allowEmailOTP: false,
  enforcedAt: null,
  gracePeriodDays: 7,
  gracePeriodActive: false,
  gracePeriodRemainingDays: null,
  isLoading: false,
  error: null,
};

export function useWorkspaceTwoFactorPolicy(
  workspaceId: string | undefined,
  workspaceFlavour?: string
): WorkspaceTwoFactorPolicy {
  const isLocal = workspaceFlavour === 'local';

  const { data, error, isLoading } = useQuery(
    workspaceId && !isLocal
      ? { query: workspacePolicyQuery, variables: { workspaceId } }
      : undefined,
    { suspense: false }
  );

  return useMemo(() => {
    if (isLocal || !workspaceId) {
      return DEFAULT_POLICY;
    }

    if (!data?.workspacePolicy) {
      return {
        ...DEFAULT_POLICY,
        isLoading,
        error: error ?? null,
      };
    }

    const p = data.workspacePolicy;
    return {
      requireTOTP: p.requireTOTP,
      allowEmailOTP: p.allowEmailOTP,
      enforcedAt: p.enforcedAt ?? null,
      gracePeriodDays: p.gracePeriodDays,
      gracePeriodActive: p.gracePeriodActive,
      gracePeriodRemainingDays: p.gracePeriodRemainingDays ?? null,
      isLoading,
      error: error ?? null,
    };
  }, [data, error, isLocal, isLoading, workspaceId]);
}
